(() => {
  'use strict';

  const namespace = 'TcgSpotlightCarousel';

  if (window[namespace]) {
    window[namespace].scan(document);
    return;
  }

  const instances = new Map();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const createCarousel = (root) => {
    if (instances.has(root)) return instances.get(root);

    const slides = Array.from(root.querySelectorAll('[data-tcg-slide]'));
    const dots = Array.from(root.querySelectorAll('[data-tcg-dot]'));
    const previousButton = root.querySelector('[data-tcg-prev]');
    const nextButton = root.querySelector('[data-tcg-next]');
    const autoplayButton = root.querySelector('[data-tcg-autoplay]');
    const autoplayLabel = root.querySelector('[data-tcg-autoplay-label]');
    const status = root.querySelector('[data-tcg-status]');

    if (!slides.length) return null;

    const cleanups = [];
    const interval = Math.max(7000, Number(root.dataset.interval) || 7000);
    let current = Math.max(0, slides.findIndex((slide) => !slide.hidden));
    let userPaused = root.dataset.autoplay !== 'true';
    let pointerInside = false;
    let focusInside = root.contains(document.activeElement);
    let timer = null;
    let focusTimer = null;
    let touchStart = null;
    let destroyed = false;

    const listen = (target, eventName, handler, options) => {
      if (!target) return;
      target.addEventListener(eventName, handler, options);
      cleanups.push(() => target.removeEventListener(eventName, handler, options));
    };

    const clearTimer = () => {
      if (timer !== null) {
        window.clearTimeout(timer);
        timer = null;
      }
    };

    const mayAutoplay = () => (
      !destroyed &&
      slides.length > 1 &&
      !userPaused &&
      !reducedMotion.matches &&
      !pointerInside &&
      !focusInside &&
      !document.hidden
    );

    const updateAutoplayControl = () => {
      if (!autoplayButton || !autoplayLabel) return;

      const unavailable = reducedMotion.matches || slides.length < 2;
      autoplayButton.disabled = unavailable;
      autoplayButton.setAttribute('aria-pressed', String(!userPaused && !unavailable));

      if (reducedMotion.matches) {
        autoplayLabel.textContent = 'Reduced motion';
        autoplayButton.setAttribute('aria-label', 'Autoplay unavailable while reduced motion is enabled');
      } else if (slides.length < 2) {
        autoplayLabel.textContent = 'Play';
        autoplayButton.setAttribute('aria-label', 'Only one slide is available');
      } else if (userPaused) {
        autoplayLabel.textContent = 'Play';
        autoplayButton.setAttribute('aria-label', 'Start carousel autoplay');
      } else {
        autoplayLabel.textContent = 'Pause';
        autoplayButton.setAttribute('aria-label', 'Pause carousel autoplay');
      }
    };

    const schedule = () => {
      clearTimer();
      if (!mayAutoplay()) return;

      timer = window.setTimeout(() => {
        show(current + 1, false);
      }, interval);
    };

    const announce = (message) => {
      if (!status) return;
      status.textContent = '';
      window.requestAnimationFrame(() => {
        if (!destroyed) status.textContent = message;
      });
    };

    const show = (requestedIndex, shouldAnnounce = true) => {
      if (destroyed) return;

      if (shouldAnnounce && slides[current].contains(document.activeElement)) root.focus({ preventScroll: true });

      current = ((requestedIndex % slides.length) + slides.length) % slides.length;

      slides.forEach((slide, index) => {
        const active = index === current;
        slide.hidden = !active;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        if (active) {
          slide.removeAttribute('inert');
        } else {
          slide.setAttribute('inert', '');
        }
      });

      dots.forEach((dot, index) => {
        const active = index === current;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', String(active));
      });

      if (shouldAnnounce) {
        const title = slides[current].dataset.slideTitle || `Slide ${current + 1}`;
        announce(`Slide ${current + 1} of ${slides.length}: ${title}`);
      }

      schedule();
    };

    const move = (direction) => show(current + direction);

    listen(previousButton, 'click', () => move(-1));
    listen(nextButton, 'click', () => move(1));

    dots.forEach((dot) => {
      listen(dot, 'click', () => {
        const index = Number(dot.dataset.slideIndex);
        if (Number.isInteger(index)) show(index);
      });
    });

    listen(autoplayButton, 'click', () => {
      if (reducedMotion.matches || slides.length < 2) return;
      userPaused = !userPaused;
      updateAutoplayControl();
      announce(userPaused ? 'Carousel autoplay paused' : 'Carousel autoplay started');
      schedule();
    });

    listen(root, 'keydown', (event) => {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;

      const target = event.target;
      if (target instanceof HTMLElement && (
        target.isContentEditable ||
        /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)
      )) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        move(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        move(1);
      }
    });

    listen(root, 'pointerenter', () => {
      pointerInside = true;
      schedule();
    });

    listen(root, 'pointerleave', () => {
      pointerInside = false;
      schedule();
    });

    listen(root, 'focusin', () => {
      focusInside = true;
      schedule();
    });

    listen(root, 'focusout', () => {
      if (focusTimer !== null) window.clearTimeout(focusTimer);
      focusTimer = window.setTimeout(() => {
        focusTimer = null;
        focusInside = root.contains(document.activeElement);
        schedule();
      }, 0);
    });

    listen(root, 'touchstart', (event) => {
      if (event.touches.length !== 1) {
        touchStart = null;
        return;
      }
      touchStart = {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY
      };
    }, { passive: true });

    listen(root, 'touchend', (event) => {
      if (!touchStart || !event.changedTouches.length) return;

      const deltaX = event.changedTouches[0].clientX - touchStart.x;
      const deltaY = event.changedTouches[0].clientY - touchStart.y;
      touchStart = null;

      if (Math.abs(deltaX) >= 50 && Math.abs(deltaX) > Math.abs(deltaY)) {
        move(deltaX < 0 ? 1 : -1);
      }
    }, { passive: true });

    listen(root, 'touchcancel', () => {
      touchStart = null;
    }, { passive: true });

    const handleEnvironmentChange = () => {
      updateAutoplayControl();
      schedule();
    };

    listen(document, 'visibilitychange', handleEnvironmentChange);

    if (typeof reducedMotion.addEventListener === 'function') {
      reducedMotion.addEventListener('change', handleEnvironmentChange);
      cleanups.push(() => reducedMotion.removeEventListener('change', handleEnvironmentChange));
    } else if (typeof reducedMotion.addListener === 'function') {
      reducedMotion.addListener(handleEnvironmentChange);
      cleanups.push(() => reducedMotion.removeListener(handleEnvironmentChange));
    }

    const instance = {
      selectBlock(blockId) {
        const index = slides.findIndex((slide) => slide.dataset.blockId === blockId);
        if (index < 0) return false;
        userPaused = true;
        updateAutoplayControl();
        show(index, false);
        return true;
      },
      destroy() {
        if (destroyed) return;
        destroyed = true;
        clearTimer();
        if (focusTimer !== null) window.clearTimeout(focusTimer);
        cleanups.splice(0).forEach((cleanup) => cleanup());
        root.removeAttribute('data-tcg-ready');
        instances.delete(root);
      }
    };

    instances.set(root, instance);
    show(current, false);
    updateAutoplayControl();
    root.setAttribute('data-tcg-ready', 'true');
    schedule();

    return instance;
  };

  const rootsWithin = (scope) => {
    const roots = [];
    if (scope instanceof Element && scope.matches('[data-tcg-carousel]')) roots.push(scope);
    if (typeof scope.querySelectorAll === 'function') {
      roots.push(...scope.querySelectorAll('[data-tcg-carousel]'));
    }
    return roots;
  };

  const scan = (scope = document) => {
    rootsWithin(scope).forEach(createCarousel);
  };

  const destroyWithin = (scope) => {
    rootsWithin(scope).forEach((root) => {
      const instance = instances.get(root);
      if (instance) instance.destroy();
    });
  };

  const manager = { scan, destroyWithin };
  window[namespace] = manager;

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const blockId = event.detail && event.detail.blockId;
    if (!blockId) return;
    for (const instance of instances.values()) {
      if (instance.selectBlock(blockId)) break;
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  } else {
    scan(document);
  }
})();
